import { useCallback } from 'react';
import { cx } from '@linaria/core';
import { Form, Formik } from 'formik';
import { string, object } from 'yup';
import { Button } from 'components/ui_library/Button';
import { InputField } from 'components/Formik/InputField';
import { SelectField } from 'components/Formik/SelectField';
import { SelectOption } from 'components/Select';
import { IconCloseSquare } from 'components/Icons';
import { Col, Row } from 'components/Grid';
import { titles } from 'styles/typography';
import { TOrganizationsMST } from 'modules/Organizations/mstStores/OrganizationMST';
import { flex } from 'styles/utils';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { cssHelpers } from 'styles/utils/core';
import { DOCUMENTATION_ORG_ROLES_LINK, ORG_ROLE_TYPE } from 'modules/Organizations/constants';
import { InfoMessage } from 'components/InfoMessage';
import { useOrgRoles } from 'modules/Organizations/hooks';
import { OrgRole } from 'modules/Organizations/components/MembersTable/OrgRole';
import { dialogWrapper, formContainer, formGroup, infoMessage } from './Modals.styles';
type Props = {
  store: TOrganizationsMST;
  onClose: () => void;
};
interface FormData {
  invitee_email: string;
  role: SelectOption;
}
const validationSchema = object().shape({
  invitee_email: string().trim().email('Invitee email is not a valid email').required('Required'),
  role: object().shape({
    name: string().required('Required'),
    value: string().required('Required')
  })
});
export const InviteModal = ({
  store,
  onClose,
  ...rest
}: Props): JSX.Element => {
  const orgId = useCurrentUserId();
  const roles = useOrgRoles(ORG_ROLE_TYPE);
  const onSubmit = useCallback(({
    invitee_email,
    role
  }: FormData) => {
    store.sendInvitation({
      orgId,
      memberEmail: invitee_email.trim(),
      memberRoleId: role.value
    });
    onClose();
  }, [store, onClose, (rest as {
    appId: string;
  }).appId, orgId]);
  return <div className={dialogWrapper}>
      <div className={cx(flex.init, flex.justifyContent.between, flex.alignItems.center)}>
        <h4 className={titles}>Invite Member</h4>

        <Button variant="unstyled" onClick={onClose}>
          <IconCloseSquare size={20} />
        </Button>
      </div>

      <div className={formContainer}>
        <Formik enableReinitialize initialValues={{
        invitee_email: '',
        role: {
          name: 'Select Role',
          value: ''
        }
      }} validationSchema={validationSchema} onSubmit={(values: FormData) => onSubmit(values)}>
          {({
          isSubmitting,
          isValid,
          dirty
        }) => <Form>
              <Row columnSpacing={2}>
                <Col xs={12}>
                  <InputField name="invitee_email" label="Email" placeholder="Enter Email" formGroupClassName={formGroup} required />
                </Col>
                <Col xs={12}>
                  <SelectField name="role" label="Role" placeholder="Select Role" options={roles} required ItemWrapper={({
                item
              }) => <OrgRole roleId={item?.value} roleName={item?.name} />} />
                  <InfoMessage className={infoMessage}>
                    For detailed roles breakdown, visit our&nbsp;
                    <a href={DOCUMENTATION_ORG_ROLES_LINK} target="_blank" rel="noreferrer">
                      doc
                    </a>
                    &nbsp;page.
                  </InfoMessage>
                </Col>
              </Row>
              <div className={cx('buttons', flex.init, flex.alignItems.center, flex.justifyContent.end, flex.gap[1], cssHelpers.margin.x[1], cssHelpers.margin.yt[1])}>
                <Button size="md" onClick={onClose} variant="tertiary">
                  Cancel
                </Button>
                <Button size="md" type="submit" disabled={isSubmitting || !isValid || !dirty}>
                  Confirm
                </Button>
              </div>
            </Form>}
        </Formik>
      </div>
    </div>;
};